import type { AxiosInstance, AxiosRequestConfig } from "axios";
import { type OperationResponse, type Parameters } from "../../common.ts";
import { omitPathParameters, replacePathParameters } from "../../utils.ts";
import type * as Paths from "./paths";

interface OperationMethods {
  "getNotes"(
    parameters?: Parameters<Paths.GetNotes.QueryParameters> | null,
    data?: null,
    config?: AxiosRequestConfig,
  ): OperationResponse<Paths.GetNotes.Responses.$200>;

  "getNote"(
    parameters: Parameters<
      Paths.GetNote.PathParameters & Paths.GetNotes.QueryParameters
    >,
    data?: null,
    config?: AxiosRequestConfig,
  ): OperationResponse<Paths.GetNote.Responses.$200>;

  "addNote"(
    parameters: Parameters<Paths.AddNote.PathParameters>,
    data: Paths.AddNote.RequestBody,
    config?: AxiosRequestConfig,
  ): OperationResponse<Paths.AddNote.Responses.$200>;

  "updateNote"(
    parameters: Parameters<Paths.UpdateNote.PathParameters>,
    data: Paths.UpdateNote.RequestBody,
    config?: AxiosRequestConfig,
  ): OperationResponse<Paths.UpdateNote.Responses.$200>;

  "deleteNote"(
    parameters: Parameters<Paths.DeleteNote.PathParameters>,
    data?: null,
    config?: AxiosRequestConfig,
  ): OperationResponse<Paths.DeleteNote.Responses.$204>;
}

export const getNoteOperations = (client: AxiosInstance) => {
  const getNotes: OperationMethods["getNotes"] = (parameters, _, config) => {
    const url = "/note";
    return client.get(replacePathParameters(url, parameters), {
      params: omitPathParameters(parameters, url),
      ...config,
    });
  };

  const getNote: OperationMethods["getNote"] = (parameters, _, config) => {
    const url = "/note/{note}";
    return client.get(replacePathParameters(url, parameters), {
      params: omitPathParameters(parameters, url),
      ...config,
    });
  };

  const addNote: OperationMethods["addNote"] = (parameters, data, config) => {
    const url = "/note/add/{type}/{id}";
    return client.post(replacePathParameters(url, parameters), data, {
      params: omitPathParameters(parameters, url),
      ...config,
    });
  };

  const updateNote: OperationMethods["updateNote"] = (
    parameters,
    data,
    config,
  ) => {
    const url = "/note/{note}";
    return client.put(replacePathParameters(url, parameters), data, {
      params: omitPathParameters(parameters, url),
      ...config,
    });
  };

  const deleteNote: OperationMethods["deleteNote"] = (
    parameters,
    _,
    config,
  ) => {
    const url = "/note/{note}";
    return client.delete(replacePathParameters(url, parameters), {
      params: omitPathParameters(parameters, url),
      ...config,
    });
  };

  return {
    getNotes,
    addNote,
    getNote,
    updateNote,
    deleteNote,
  };
};
